const defaultLang = "en";

/**
 * 统一的语言/国家/货币配置
 * 新增语言只需在此添加一个配置对象，系统会自动支持
 * 
 * 配置说明：
 * - code: 语言代码，用于前端路由和JSON文件命名
 * - displayName: 语言显示名称，用于语言切换下拉菜单
 * - country: 国家代码，用于后端接口获取对应语言的商品数据
 * - currency: 货币代码，用于支付和价格显示
 * - symbol: 货币符号，用于价格显示
 * - paymentMethods: 支持的支付方式列表
 * - enabled: 是否启用该语言
 */
export const LANGUAGE_CONFIG = {
    cn: {
        code: 'cn',
        displayName: '简体中文',
        country: 'CN',
        currency: 'CNY',
        symbol: '¥',
        paymentMethods: ['wxpay', 'alipay'],
        enabled: true
    },
    en: {
        code: 'en',
        displayName: 'English',
        country: 'US',
        currency: 'USD',
        symbol: 'USD',
        paymentMethods: ['paypal', 'card'],
        enabled: true
    },
    tw: {
        code: 'tw',
        displayName: '繁體中文',
        country: 'TW',
        currency: 'TWD',
        symbol: 'TWD',
        paymentMethods: ['jkopay', 'paypal'],
        enabled: true
    },
};

/**
 * 获取启用的语言列表
 */
export function getEnabledLanguages() {
    return Object.values(LANGUAGE_CONFIG).filter(lang => lang.enabled);
}

/**
 * 获取语言显示名称映射对象
 */
export function getLanguageDisplayNames() {
    const names = {};
    Object.values(LANGUAGE_CONFIG).forEach(lang => {
        if (lang.enabled) {
            names[lang.code] = lang.displayName;
        }
    });
    return names;
}

export const globalConfig = {
    currentLang: defaultLang,
    displayLang: LANGUAGE_CONFIG[defaultLang]?.displayName || 'English',
    logoImg: require(`@/assets/img/logo_${defaultLang}.png`)
};

/**
 * 根据语言代码获取货币代码
 */
export function GetCurrencyByLang(lang) {
    return LANGUAGE_CONFIG[lang]?.currency || 'USD';
}

/**
 * 根据语言代码获取国家代码
 */
export function GetCountryByLang(lang) {
    return LANGUAGE_CONFIG[lang]?.country || 'US';
}

/**
 * 根据国家代码获取语言代码
 */
export function GetLangByCountry(ctr) {
    const lang = Object.values(LANGUAGE_CONFIG).find(
        l => l.country === ctr && l.enabled
    );
    return lang?.code || 'en';
}

/**
 * 根据货币代码获取国家代码
 */
export function GetCountryByCurrency(currency) {
    const lang = Object.values(LANGUAGE_CONFIG).find(
        l => l.currency === currency && l.enabled
    );
    return lang?.country || 'US';
}

/**
 * 根据国家代码获取货币代码
 */
export function getCurrencyByCountry(country) {
    const lang = Object.values(LANGUAGE_CONFIG).find(
        l => l.country === country && l.enabled
    );
    return lang?.currency || 'USD';
}

/**
 * 根据货币代码获取货币符号
 */
export function getCurrencySymbol(currency) {
    const lang = Object.values(LANGUAGE_CONFIG).find(
        l => l.currency === currency
    );
    return lang?.symbol || '$';
}

/**
 * 根据货币代码获取支付方式列表
 */
export function getPaymentMethods(currency) {
    const lang = Object.values(LANGUAGE_CONFIG).find(
        l => l.currency === currency && l.enabled
    );
    return lang?.paymentMethods || ['paypal', 'card'];
}

/**
 * 从后端获取域名配置
 */
export async function fetchDomainConfig() {
    try {
        const response = await fetch('/order/domainconfig');
        const result = await response.json();
        if (result.code === 200) {
            return result.data;
        }
    } catch (error) {
        console.error('Failed to fetch domain config:', error);
    }

    return {
        defaultLang: 'en',
        forceCurrency: false,
        currency: '',
        country: ''
    };
}
